import { EventType, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  type IMsalContext,
  MsalAuthenticationTemplate,
  MsalConsumer,
  MsalProvider,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
// Font CSS now loaded in index.html for faster first paint
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import React from 'react';
import { Userpilot } from 'userpilot';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import appInsights from './appInsights';
import { queryClient } from './queryClient';
import { msalConfig } from './common/AuthConfig';
import { setupAvatarFetcher } from './common/avatarFetcher';
import { getAuthClient, isAuth0Provider } from './common/auth/authClient';
import reportWebVitals from './common/ReportWebVitals';
import './i18n/i18nConfig'; // Initialize i18n
import { AppDataProvider } from './providers/AppDataProvider';
import { LocaleProvider } from './providers/LocaleProvider';
import { SnackbarProvider } from './providers/SnackbarProvider';
import './resources/css/app.css';
import './resources/css/colors.css';
import './resources/css/fonts.css';
import './resources/css/print.css';
import store from './store';
import { bootstrapPreview } from './views/try/preview/bootstrapPreview';
import {
  hasPreviewIntent,
  installPreviewBfcacheGuard,
  primeArmedFromIntent,
} from './views/try/preview/previewIdentity';
import { AuthLoadingView } from './views/other/AuthLoadingView';
import { NotLoggedInView } from './views/other/NotLoggedInView';
import { LoginEvent } from 'klayowebshared';

/**
 * Initialize a PublicClientApplication instance which is provided to the MsalProvider component
 * We recommend initializing this outside of your root component to ensure it is not re-initialized on re-renders
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Try It Now preview sandbox: derive the armed state from the sessionStorage intent at the very
// top, BEFORE the analytics SDKs initialize, so the egress kill-switch can suppress them.
primeArmedFromIntent();
// Escape a stale armed "zombie" page restored from the back/forward cache after exiting a demo.
installPreviewBfcacheGuard();

/*msalInstance.addEventCallback(message => {
    if (message.eventType === EventType.LOGIN_SUCCESS) {
        //Utils.deleteCookie('Impersonate');
        //var domain = new URL(window.app.env.API_URL).hostname;
        //document.cookie = `DemoMode=; expires = Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain};path=/;`; //samesite=none;`;
    }
});*/

// Userpilot in-app onboarding/tours. Loaded for EVERY session — including the "Try It Now" preview —
// because trial prospects need the demo-page journeys. Unlike the analytics SDKs below, Userpilot
// carries no real PII in preview: identify() uses a per-session synthetic id (see previewUserpilotId)
// instead of the prospect's employeeId, page views are synthetic routes, and events are static
// names. Userpilot is not a session-replay tool, so it does not exfiltrate the DOM chrome text.
// identify()/reload() are wired in App.tsx.
Userpilot.initialize('NX-db1df07e');

// Entering preview from an already-logged-in session: Userpilot persists the previously identified
// real user across the hard reload, and App.tsx's first reload() on mount can fire before the
// synthetic identify() runs — attributing that first demo page view to the real employeeId. Reset
// here so the preview session starts with no identity until the synthetic id is set.
if (hasPreviewIntent()) {
  Userpilot.reset();
}

// Egress kill-switch: in the preview sandbox, do NOT initialize the analytics SDKs — they would send
// the prospect's real identity (name/org/employeeId) and page DOM to third parties (App Insights,
// PostHog, and downstream Hotjar/GTM via Analytics). Key off the preview INTENT, not the armed flag:
// "Show my team" arms only after its async read, but must be suppressed from the very first
// synchronous moment. Boot analytics only for a genuine non-preview session.
if (!hasPreviewIntent()) {
  appInsights.loadAppInsights();
  appInsights.trackPageView();

  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    // ui_host points at PostHog itself so the toolbar and in-app links resolve to PostHog
    // even though api_host is our first-party reverse proxy (e.klayo.com).
    ui_host: 'https://us.posthog.com',
    // No autocapture: all product events are defined explicitly via posthog.capture(...).
    autocapture: false,
    loaded: (posthog) => {
      if (import.meta.env.DEV) posthog.debug();
    },
  });
  // Undo any opt-out left behind by a prior preview in this browser (opt-out persists in
  // localStorage); without this, a real session after a demo would stay silently un-tracked.
  posthog.opt_in_capturing();

  // A fresh interactive login completes via a B2C redirect back into the app; MSAL raises
  // LOGIN_SUCCESS only for that (not for cached/silent sessions), so it's our login:success signal.
  msalInstance.addEventCallback((message) => {
    if (message.eventType === EventType.LOGIN_SUCCESS) {
      posthog.capture(LoginEvent.Success);
    }
  });
} else {
  // Defence-in-depth: even though init is skipped when armed, explicitly opt out so a future
  // change (or autocapture-on-mount of <PostHogProvider>) can't silently start sending events.
  posthog.opt_out_capturing();
}

const appTree = (msalContext?: IMsalContext) => (
  <BrowserRouter>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AppDataProvider>
          <LocaleProvider>
            <SnackbarProvider>
              <App msal={msalContext} />
            </SnackbarProvider>
          </LocaleProvider>
        </AppDataProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} position='bottom' /> */}
      </QueryClientProvider>
    </Provider>
  </BrowserRouter>
);

// B2C: MSAL drives the login redirect and renders the app once authenticated.
const msalApp = (
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <MsalProvider instance={msalInstance}>
        <MsalConsumer>
          {(msalContext) => (
            <MsalAuthenticationTemplate
              interactionType={InteractionType.Redirect}
              errorComponent={(props) => <NotLoggedInView {...props} msal={msalContext} />}
              loadingComponent={AuthLoadingView}
            >
              <AuthenticatedTemplate>{appTree(msalContext)}</AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                <NotLoggedInView msal={msalContext} />
              </UnauthenticatedTemplate>
            </MsalAuthenticationTemplate>
          )}
        </MsalConsumer>
      </MsalProvider>
    </PostHogProvider>
  </React.StrictMode>
);

// Auth0: startApp() has already completed the login before rendering.
const auth0App = (
  <React.StrictMode>
    <PostHogProvider client={posthog}>{appTree()}</PostHogProvider>
  </React.StrictMode>
);

/**
 * Auth0 sign-in runs here, before render: the preview bootstrap below makes authenticated reads
 * before the first render, and a React effect would run the one-shot redirect callback twice under
 * StrictMode. Returns false when the page is navigating to the login page or showing an error.
 */
async function startAuth0(render: (node: React.ReactNode) => void): Promise<boolean> {
  try {
    const client = getAuthClient(); // throws on an insecure origin (no window.crypto)
    const loginCompleted = await client.initialize();
    // Same signal as MSAL's LOGIN_SUCCESS: an interactive login just completed (not a cached session).
    if (loginCompleted && !hasPreviewIntent()) {
      posthog.capture(LoginEvent.Success);
    }

    if (!client.getAccount()) {
      render(<AuthLoadingView {...({} as IMsalContext)} />);
      await client.loginRedirect();
      return false;
    }
    return true;
  } catch (error) {
    // E.g. a callback denied by a Post-Login Action. No auto-redirect: it would loop.
    console.error(error);
    render(<NotLoggedInView />);
    return false;
  }
}

// Seed React Query caches with the synthetic identity (no-op when there's no preview intent). For
// "Show my team" this also performs the real student read and arms the fail-closed mock. Awaited so
// the first render already sees primed caches / armed state.
async function startApp() {
  const root = ReactDOM.createRoot(document.getElementById('root')!);
  setupAvatarFetcher();

  if (isAuth0Provider()) {
    if (await startAuth0((node) => root.render(node))) {
      await bootstrapPreview(queryClient);
      root.render(auth0App);
    }
    return;
  }

  // MSAL v4 requires initialize() before any token call. MsalProvider also initializes during
  // render, but the preview bootstrap below can make an authenticated read ("Show my team") BEFORE
  // render, so ensure the instance is ready first. Idempotent — MsalProvider's own init becomes a
  // no-op. Guarded so an init failure still falls through to the normal render path.
  try {
    await getAuthClient().initialize();
  } catch {
    /* MsalProvider surfaces auth errors during render as before */
  }

  // Force a one-time re-login when auth config changes materially (e.g. B2C custom-domain switch).
  // Whenever the delivered AUTH_CACHE_VERSION differs from the value last stored in this browser —
  // including the first time it is introduced — the stale MSAL cache is cleared so the normal login
  // flow re-mints a token under the current authority. Deploys that leave AUTH_CACHE_VERSION
  // unchanged are a no-op, so routine releases don't log anyone out. Plain string compare against the
  // delivered config value (not the build version). Runs after initialize() (required before cache
  // access) and before render. Fully guarded: a storage/clearCache failure must not block render —
  // same rationale as the initialize() guard above.
  try {
    const requiredAuthCacheVersion = window.app.env.AUTH_CACHE_VERSION ?? '';
    const authCacheVersionKey = 'klayo.authCacheVersion';
    if (
      requiredAuthCacheVersion &&
      localStorage.getItem(authCacheVersionKey) !== requiredAuthCacheVersion
    ) {
      if (msalInstance.getAllAccounts().length > 0) {
        await msalInstance.clearCache();
      }
      localStorage.setItem(authCacheVersionKey, requiredAuthCacheVersion);
    }
  } catch {
    /* best-effort: fall through to the normal render/login path */
  }

  await bootstrapPreview(queryClient);
  root.render(msalApp);
}
startApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
